// Procédures d'incident (page Incidents → Procédures). Texte de l'équipe, en français.
// À tenir à jour après chaque bilan d'incident.

export const SEVERITY_GUIDE = [
  {
    level: "sev1",
    title: "SEV1 — Service coupé",
    when: "Le site ou l'appli ne s'ouvre plus, aucune vidéo ne se lit, ou personne ne peut payer.",
    response: "Réaction immédiate, jour et nuit. Un responsable nommé dans les 15 minutes. Point toutes les 30 minutes jusqu'à la résolution.",
  },
  {
    level: "sev2",
    title: "SEV2 — Fonction importante dégradée",
    when: "Lenteurs fortes, un moyen de paiement en panne, notifications ou e-mails bloqués, une partie des utilisateurs touchée.",
    response: "Prise en charge dans l'heure (heures ouvrées, ou le soir d'une sortie importante). Point toutes les 2 heures.",
  },
  {
    level: "sev3",
    title: "SEV3 — Gêne limitée",
    when: "Problème contournable ou touchant peu de personnes (un écran, un pays, un opérateur).",
    response: "Traité dans la journée ouvrée suivante.",
  },
];

export const PROTOCOL = [
  "Constater : onglet Santé (quel composant est rouge ou orange ?), alertes reçues par e-mail, retours des utilisateurs.",
  "Déclarer l'incident ici, avec sa gravité et les composants touchés. Un incident ouvert par une alerte ou par la surveillance de santé est déjà créé : le compléter. Pour un SEV1 ou un SEV2, le support (sys@afomedia.com) reçoit aussitôt un e-mail avec la marche à suivre.",
  "Prendre la responsabilité (« Je m'en occupe ») : une seule personne pilote, les autres l'aident.",
  "Prévenir : l'équipe (groupe WhatsApp), puis les utilisateurs si l'incident se voit (réseaux sociaux, et l'appli si besoin). Ne jamais donner de détail technique ni de cause supposée.",
  "Suivre la procédure du composant touché (ci-dessous) et noter chaque action dans la chronologie de l'incident.",
  "Passer en « Corrigé, sous surveillance » dès que le service revient, puis « Résolu » après au moins 30 minutes sans rechute (onglet Santé au vert).",
  "Écrire le bilan dans les 48 heures : cause, correction, actions pour que cela ne se reproduise pas. Les actions deviennent des tâches.",
];

export const RUNBOOKS = [
  {
    id: "api",
    title: "L'API ne répond plus ou est très lente",
    signs: "Santé : API rouge ou orange (temps de réponse p95 > 1,5 s, erreurs, « boucle » > 200 ms). Le site et l'appli chargent sans fin.",
    steps: [
      "Sur le serveur : `pm2 status` (le processus tourne-t-il ? redémarre-t-il en boucle ?), puis `pm2 logs --lines 200` pour la dernière erreur.",
      "Disque et mémoire : `df -h` (disque plein = journaux ou fichiers envoyés) et `free -m`.",
      "Si l'API répond lentement mais répond : ne pas la redémarrer d'abord. En mode cluster, `pm2 reload afo-api-cluster` redémarre les processus l'un après l'autre, sans coupure.",
      "Si la lenteur suit une mise à jour : revenir à la version précédente (`git log --oneline -5`, `git checkout <commit précédent>`, puis `pm2 reload <nom>` ; `pm2 restart` en mode simple coupe quelques secondes).",
      "Si c'est un pic d'audience (sortie d'une vidéo, notification) : voir la procédure « Pic d'audience ».",
      "En dernier recours : `pm2 restart all` (coupe les requêtes en cours quelques secondes).",
    ],
  },
  {
    id: "spike",
    title: "Pic d'audience (sortie d'une vidéo, notification à tous)",
    signs: "Requêtes par minute très au-dessus de la normale, temps de réponse qui monte, base de données lente.",
    steps: [
      "Avant une grosse sortie : programmer la notification à une heure creuse plutôt qu'en pointe, éviter d'envoyer plusieurs notifications à tous la même heure.",
      "Pendant : surveiller l'onglet Santé (p95, boucle, base). Les vidéos elles-mêmes sont servies par JW Player et ne chargent pas notre serveur.",
      "Si l'API ralentit (p95 > 1,5 s, boucle > 200 ms) : activer le MODE AFFLUENCE (Maintenance → Mode affluence). Il ne s'active jamais tout seul. Effet en 5 s : caches plus longs, requêtes secondaires refusées quand un processus est plein, bandeau sur le site, notifications programmées et relances reportées. L'essentiel (ouvrir une vidéo, Premium, connexion, paiements) passe toujours.",
      "Ne pas envoyer de notification à tout le monde tant que le mode affluence est actif.",
      "Si l'API sature (boucle > 200 ms, CPU du processus à 100 %) : passer PM2 en mode cluster (un processus par cœur, voir docs/capacite.md).",
      "Si la base sature (MongoDB Atlas : CPU ou connexions au maximum) : monter le cluster Atlas d'un palier (se fait sans coupure, en quelques minutes).",
      "Retour au calme (p95 < 500 ms pendant 30 min) : désactiver le mode affluence. Les notifications et relances reportées repartent à leur prochain passage.",
      "Après : noter dans le bilan le nombre de requêtes par minute atteint, pour ajuster les prévisions.",
    ],
  },
  {
    id: "database",
    title: "Base de données (MongoDB Atlas) en panne ou saturée",
    signs: "Santé : base rouge (déconnectée) ou orange (ping > 500 ms). Erreurs 500 partout.",
    steps: [
      "Atlas → Clusters : état du cluster, alertes, graphiques CPU / connexions / opérations.",
      "Atlas → Performance Advisor : requêtes lentes et index proposés.",
      "Statut du fournisseur : status.cloud.mongodb.com.",
      "Saturation : monter d'un palier (Edit configuration) ; connexions au maximum : redémarrer l'API (`pm2 restart all`).",
      "Données supprimées ou corrompues : restaurer depuis une sauvegarde Atlas dans un NOUVEAU cluster, vérifier, puis copier seulement ce qui manque. Ne jamais écraser la base en service sans vérification.",
    ],
  },
  {
    id: "jwplayer",
    title: "Les vidéos ne se lisent plus / l'envoi de vidéos échoue (JW Player)",
    signs: "Santé : JW Player rouge. Lecteur noir sur le site et l'appli, ou erreur à l'envoi d'une vidéo.",
    steps: [
      "Statut du fournisseur : status.jwplayer.com. Si JW est en panne : prévenir les utilisateurs, rien d'autre à faire de notre côté.",
      "« Clé refusée » (HTTP 401/403) : créer une nouvelle clé API dans le tableau de bord JW, la mettre dans le .env du serveur (JW_PLAYER_API_KEY), `pm2 restart all`.",
      "Vérifier aussi le quota et la facturation du compte JW (lecture bloquée si le forfait est dépassé).",
    ],
  },
  {
    id: "stripe",
    title: "Paiements par carte en échec (Stripe)",
    signs: "Alerte « Pic d'échecs de paiement » ou « Tentatives de paiement sans succès » (des personnes essaient de payer et aucune n'y arrive ; une journée sans aucune tentative n'est pas un incident), Santé : Stripe rouge.",
    steps: [
      "Statut du fournisseur : status.stripe.com.",
      "Tableau de bord Stripe → Développeurs → Webhooks : les envois vers /api/payments/stripe/webhook réussissent-ils ? Sinon, les relancer.",
      "Paiements → Échecs : refus des banques (normal en petit nombre) ou erreur technique (clé, compte) ?",
      "Clé expirée ou révoquée : nouvelle clé dans Stripe, .env (STRIPE_KEY), `pm2 restart all`.",
      "Abonnements impayés après l'incident : page « Relances impayés ».",
    ],
  },
  {
    id: "mobilemoney",
    title: "Mobile Money en panne (pawaPay, CinetPay)",
    signs: "Alerte « Mobile Money en panne », Santé : pawaPay rouge, clients qui signalent des paiements bloqués.",
    steps: [
      "Audit et logs → Mobile Money : état de chaque prestataire, dernières notifications reçues.",
      "Suspendre le prestataire en panne (même page) : les paiements passent par l'autre, ou le Mobile Money est masqué si aucun ne fonctionne.",
      "Paiements restés « en attente » : les revérifier depuis la fiche du paiement une fois le prestataire revenu.",
      "Réactiver le prestataire quand il fonctionne de nouveau.",
    ],
  },
  {
    id: "firebase",
    title: "Firebase en panne (notifications, configuration de l'appli, directs)",
    signs: "Santé : Firebase rouge. Notifications qui ne partent plus, directs ou configuration de l'appli illisibles.",
    steps: [
      "Statut du fournisseur : status.firebase.google.com.",
      "Notifications programmées : elles partent au rétablissement (sinon les renvoyer depuis Notifications → Historique).",
      "Clé du compte de service refusée : générer une nouvelle clé dans la console Firebase et la remplacer sur le serveur (dossier keys/), `pm2 restart all`.",
    ],
  },
  {
    id: "email",
    title: "E-mails non reçus (Mailgun)",
    signs: "Santé : Mailgun rouge, page « E-mails envoyés » pleine de rejets, utilisateurs sans e-mail de confirmation ou de reçu.",
    steps: [
      "Page « E-mails envoyés » : filtrer « Échecs » et lire la raison (adresse invalide, boîte pleine, refus pour spam…).",
      "Statut du fournisseur : status.mailgun.com.",
      "Refus pour spam en série : vérifier les enregistrements DNS (SPF, DKIM, DMARC) du domaine d'envoi dans Mailgun.",
    ],
  },
  {
    id: "certificate",
    title: "Certificat HTTPS expiré (site ou API inaccessibles « connexion non sécurisée »)",
    signs: "Les navigateurs et l'appli refusent la connexion à l'API.",
    steps: [
      "Sur le serveur : `sudo certbot renew`, puis `pm2 restart all` (l'API lit le certificat au démarrage).",
      "Vérifier le renouvellement automatique : `sudo certbot renew --dry-run`.",
    ],
  },
  {
    id: "security",
    title: "Sécurité : clé divulguée, compte admin compromis, comportement suspect",
    signs: "Actions inconnues dans le journal des admins, clé publiée par erreur, alerte d'un fournisseur.",
    steps: [
      "Compte admin : `node scripts/resetAdminPassword.js <email>` (déconnecte ce compte partout), puis vérifier ses actions dans Audit et logs → Activité des admins.",
      "Clé d'un fournisseur (Stripe, JW Player, Mailgun, pawaPay, Firebase) : la révoquer chez le fournisseur, en créer une nouvelle, mettre à jour le .env, `pm2 restart all`.",
      "Jetons des utilisateurs ou des admins : changer leur clé de signature déconnecte tout le monde (à faire en cas de fuite du code).",
      "Ne rien supprimer : garder les journaux pour comprendre ce qui s'est passé.",
    ],
  },
];
